import * as React from "react";
import { CSSTransition } from "react-transition-group";

import { isBrowser } from "@montana/helpers/browser.helper";
import { Portal } from "@montana/shared/portal";
import { ClickOutsideDetect } from "@montana/shared/click-outside-detect";
import { disableScrollLock, enableScrollLock } from "@montana/helpers/scroll-lock.helper";
import { SvgIcon } from "@montana/shared/svg-icon";
import { Button, ButtonStyles } from "@montana/shared/button";
import { IButton } from "@montana/shared/button/button";

interface IModalProps {
    animationDuration?: number; // Default: 300
    button?: IButton;
    className?: string; // Default: ""
    containerClassName?: string; // Default: ""
    disableBackgroundInteraction?: boolean; // Default: true
    includeBackdrop?: boolean; // Default: true
    includeExitButton?: boolean; // Default: true
    menuMode?: boolean; // Default: false
    onModalClosed?: () => void;
    position?: "left" | "right" | "center" | "fullscreen"; // Default "right"
    preventClose: boolean;
    ref: React.RefObject<Modal>;
    style?: React.CSSProperties;
    timeout?: {
        appear?: number;
        enter?: number;
        exit?: number;
    };
}

interface IModalState {
    isExiting: boolean;
    isOpen: boolean;
    isOpenAnimationComplete: boolean;
}

const DISABLE_INTERACTION_CLASS = "disable-interaction";

/**
 * @author SBF
 * @description
 * Renders a modal with children as content. To actually open the modal, call open on the instance of the modal component
 * assigned by reference from outer component.
 *
 * @param {string} animationClassNames a string representing the set of classes to be used when animating in/out
 * @param {React.RefObject<Modal>} ref a react ref object to assign component instance to. Used for accessing public API of component.
 *
 * @function open opens the modal, optionally animating in.
 * @function close closes the modal, optionally animating out.
 *
 * @example
 *  class ComponentWithModal extends React.Component {
 *      private ref = React.createRef<Modal>();
 *
 *      private openModal(): void {
 *          // As ref is null on the first render, this check is needed. This is also why ref.current!.open cannot be assigned to e.g. button event.
 *          if (this.ref.current != null) {
 *              this.ref.current!.open();
 *          }
 *      }
 *
 *      public render(): JSX.Element {
 *          return <React.Fragment>
 *              <button onClick={this.openModal.bind(this)}></button>
 *              <Modal ref={this.ref} className="component-with-modal__modal" animationClassNames="fade"> // Assign ref
 *                  <div>This is the content of the modal!</div>
 *              </Modal>
 *          </React.Fragment>;
 *      }
 *  }
 */
export class Modal extends React.Component<IModalProps, IModalState> {


    public static toggleBackgroundInteraction(isEnabled: boolean): void {
        if (isBrowser) {
            if (isEnabled) {
                document.body.classList.remove(DISABLE_INTERACTION_CLASS);
            } else {
                document.body.classList.add(DISABLE_INTERACTION_CLASS);
            }
        }
    }

    public static defaultProps: Readonly<Partial<IModalProps>> = {
        animationDuration: 300,
        timeout: {
            appear: 300,
            enter: 0,
            exit: 300,
        },
        className: "",
        containerClassName: "",
        includeBackdrop: true,
        includeExitButton: true,
        disableBackgroundInteraction: true,
        menuMode: false,
        preventClose: false,
        position: "right",
    };

    public state: Readonly<IModalState> = {
        isExiting: false,
        isOpen: false,
        isOpenAnimationComplete: false,
    };


    public componentDidMount(): void {
        Modal.toggleBackgroundInteraction(true);
        document.addEventListener("keydown", this.handleKeyDown);
    }


    public componentWillUnmount(): void {
        const { disableBackgroundInteraction } = this.props;

        if (disableBackgroundInteraction && isBrowser) {
            Modal.toggleBackgroundInteraction(true);
        }

        disableScrollLock();

        document.removeEventListener("keydown", this.handleKeyDown);
    }

    public open = (): void => {
        const { disableBackgroundInteraction } = this.props;
        const { isOpen } = this.state;

        if (isOpen) {
            return;
        }

        this.setState({ isOpen: true });

        enableScrollLock();

        if (disableBackgroundInteraction && isBrowser) {
            Modal.toggleBackgroundInteraction(false);
        }
    };


    public close = (): void => {
        const { isOpen } = this.state;

        disableScrollLock();

        if (!isOpen) {
            return;
        }


        this.setState({ isExiting: true });
    };


    private enterAnimationDone = (): void => {
        this.setState({ isOpenAnimationComplete: true });
    };


    private exitAnimationDone = (): void => {
        const { disableBackgroundInteraction } = this.props;
        this.setState({ isExiting: false, isOpen: false, isOpenAnimationComplete: false });

        if (this.props.onModalClosed != null) {
            this.props.onModalClosed();
        }

        if (disableBackgroundInteraction && isBrowser) {
            Modal.toggleBackgroundInteraction(true);
        }
    };


    private handleKeyDown = (event: KeyboardEvent): void => {
        if (!this.props.preventClose) {
            if (event.key === "Escape" || event.key === "Esc") {
                this.close();
            }
        }
    };


    public render(): JSX.Element | null {
        const {
            animationDuration,
            button,
            children,
            className,
            containerClassName,
            includeBackdrop,
            includeExitButton,
            menuMode,
            position,
            style,
            timeout,
        } = this.props;
        const { isExiting, isOpen } = this.state;

        if (!isOpen) {
            return null;
        }

        const container = (
            <CSSTransition
                timeout={{
                    appear: (timeout && timeout.appear) || animationDuration as number + 20, // These +20's is a little fix for janky animation that might occur, because of the use of setTimeout
                    enter: (timeout && timeout.enter) || 0,
                    exit: (timeout && timeout.exit) || animationDuration as number + 20,
                }}
                in={!isExiting}
                appear
                enter
                exit
                onEntered={this.enterAnimationDone}
                unmountOnExit
                classNames={`modal__container--animate-${position}`}
                onExited={this.exitAnimationDone}
            >
                <>
                    <div
                        className={`modal__container modal__container--${position}${button ? " modal__container--has-button" : ""} ${className}`}
                        style={{ ...style, transitionDuration: `${animationDuration}ms` }}
                    >
                        <section className="modal__inside">
                            <div className="modal__scroll-area">
                                {includeExitButton && !this.props.preventClose && (
                                    <div className="modal__exit">
                                        <button type="button" onClick={this.close} className="modal__exit-button">
                                            <SvgIcon className="modal__exit-icon" iconName="close" />
                                        </button>
                                    </div>
                                )}
                                <div className="modal__content">
                                    {children}
                                </div>
                            </div>
                        </section>
                    </div>
                    {button
                        ? (
                            <CSSTransition
                                timeout={{
                                    appear: (timeout && timeout.appear) || animationDuration as number + 20, // These +20's is a little fix for janky animation that might occur, because of the use of setTimeout
                                    enter: (timeout && timeout.enter) || 0,
                                    exit: (timeout && timeout.exit) || animationDuration as number + 20,
                                }}
                                in={!isExiting}
                                appear
                                enter
                                exit
                                unmountOnExit
                                classNames="modal__button-animate"
                            >
                                <div className="modal__button" style={{ transitionDuration: `${animationDuration}ms` }}>
                                    <Button style={ButtonStyles.Primary} {...button} />
                                </div>
                            </CSSTransition>
                        )
                        : null}
                </>
            </CSSTransition >
        );


        // The modal needs to be an element INSIDE the portal and not the portal itself.
        // The portal-element doesn't update when props change, but its content does.
        return (
            <Portal>
                <div className={`modal modal--${position} ${menuMode ? "modal--menu-mode" : ""} ${containerClassName}`}>
                    {includeBackdrop
                        ? (
                            <CSSTransition
                                timeout={{
                                    appear: (timeout && (menuMode ? timeout.exit : timeout.appear)) || animationDuration as number + 20, // These +20's is a little fix for janky animation that might occur, because of the use of setTimeout
                                    enter: (timeout && timeout.enter) || 0,
                                    exit: (timeout && (menuMode ? timeout.appear : timeout.exit)) || animationDuration as number + 20,
                                }}
                                in={!isExiting}
                                appear
                                enter
                                exit
                                unmountOnExit
                                classNames="modal__backdrop-animate"
                            >
                                <div className="modal__backdrop" style={{ transitionDuration: `${animationDuration}ms` }} />
                            </CSSTransition>
                        )
                        : null}

                    {!this.props.preventClose && this.props.position !== 'fullscreen' ? (
                        <ClickOutsideDetect onClickOutside={this.close}>
                            {container}
                        </ClickOutsideDetect>
                    ) : container}
                </div>
            </Portal>
        );
    }
}
